/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Minimal ISO Base Media File Format (ISO/IEC 14496-12) box scanner. Just enough
 * to walk top-level and container boxes and locate the C2PA `uuid` box — no
 * codec/sample parsing. Inspired by the SVTA common-media-library iso-bmff
 * reader (Apache-2.0) but written from scratch.
 */

/** A parsed BMFF box. Offsets are absolute into the scanned buffer. */
export interface Box {
    /** 4-character box type, e.g. `moov`, `mdat`, `uuid`. */
    type: string
    /** Absolute offset of the box start (its size field). */
    start: number
    /** Total box size in bytes (header + payload). */
    size: number
    /** Header length: 8, or 16 for 64-bit `largesize`. Excludes the 16-byte usertype of `uuid` boxes. */
    headerSize: number
    /** Absolute offset where the payload begins (after header; for `uuid`, before the 16-byte usertype). */
    payloadStart: number
    /** Absolute offset one past the box end (`start + size`). */
    end: number
    /** For `uuid` boxes: the 16-byte extended type. */
    usertype?: Uint8Array
}

/**
 * Scans the sibling boxes in `[start, end)` of `u8` (non-recursive).
 *
 * Tolerant of a trailing truncated box (returns what parsed cleanly). `size === 0`
 * means "to end of buffer"; `size === 1` means a 64-bit largesize follows the type.
 */
export function scanBoxes (u8: Uint8Array, start = 0, end = u8.byteLength): Box[] {
    const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength)
    const boxes: Box[] = []
    let off = start
    while (off + 8 <= end) {
        let size = dv.getUint32(off, false)
        const type = fourCC(u8, off + 4)
        let headerSize = 8
        if (size === 1) {
            if (off + 16 > end) break
            const hi = dv.getUint32(off + 8, false)
            const lo = dv.getUint32(off + 12, false)
            size = hi * 2 ** 32 + lo
            headerSize = 16
        } else if (size === 0) {
            size = end - off
        }
        if (size < headerSize || off + size > end) break
        const box: Box = {
            type,
            start: off,
            size,
            headerSize,
            payloadStart: off + headerSize,
            end: off + size
        }
        if (type === 'uuid' && box.payloadStart + 16 <= end) {
            box.usertype = u8.subarray(box.payloadStart, box.payloadStart + 16)
        }
        boxes.push(box)
        off += size
    }
    return boxes
}

/** Reads a 4-character box type at `off`. */
export function fourCC (u8: Uint8Array, off: number): string {
    return String.fromCharCode(u8[off], u8[off + 1], u8[off + 2], u8[off + 3])
}

/** True if a `uuid` box's usertype equals `expected` (16 bytes). */
export function usertypeEquals (box: Box, expected: Uint8Array): boolean {
    const t = box.usertype
    if (!t || t.length !== 16 || expected.length !== 16) return false
    for (let i = 0; i < 16; i++) {
        if (t[i] !== expected[i]) return false
    }
    return true
}
